import { Option } from 'modules/dashboard/pages/FirstApprovalScreen/index';
import { Status } from 'shared/enums';
import { Artist } from 'types';
import { EditPuzzleField, editPuzzleShema, UpdateField, updateSchema } from 'validations';
import { coloringGridDBSchema } from 'validations/coloringGridSchema';
import { reactSelectValidator } from 'validations/select';
import { array, InferType, number, object, string, ValidationError } from 'yup';

const singlePuzzleSchema = editPuzzleShema()
  .concat(
    object({
      puzzleId: string().required(),
      premiumTier: reactSelectValidator({ valueValidator: number() }).nullable(),
      [EditPuzzleField.Status]: reactSelectValidator({
        valueValidator: string()
          .required()
          .matches(/rejected|approved/, 'Status must be either approved or rejected'),
      }),
    }),
  )
  .test('artist-status-link', (value, context) => {
    const artistStatus = context.from?.[1].value.artist.status?.value;
    const puzzleStatus = value.status?.value;
    const rejectMessage = value?.rejectMessage;

    // All puzzles must be rejected when artist is rejected.
    if (artistStatus === 'rejected' && puzzleStatus !== 'rejected') {
      throw new ValidationError(
        'Cannot approve puzzle while artist is rejected',
        puzzleStatus,
        `${context.path}.status`,
        'string',
      );
    }

    // Reject reason is only required for rejected puzzles if artist is approved or pending.
    if (puzzleStatus === Status.Rejected && artistStatus !== Status.Rejected && !rejectMessage) {
      throw new ValidationError(
        'Must provide reject reason, or change status',
        puzzleStatus,
        `${context.path}.status`,
        'string',
      );
    }

    return true;
  });

export const singleGridSchema = coloringGridDBSchema.shape({
  gridId: string().required(),
  collections: array()
    .of(reactSelectValidator({ valueValidator: string().required() }))
    .required(),
  publisher: reactSelectValidator({ valueValidator: string().required() }),
  status: reactSelectValidator({ valueValidator: string().required() }),
  keywords: string()
    .trim()
    .matches(
      /^(\S{3,15})(\s\S{3,15}){2,11}$/g,
      'Keywords must be space separated 3-15 characters long (3-15 keywords)',
    )
    .required(),
});

export type PuzzleFormFields = InferType<typeof singlePuzzleSchema>;
export type GridFormFields = InferType<typeof singleGridSchema>;

export const getFirstApprovalFormSchema = (artistId: string, artistData: Artist) => {
  return object({
    puzzles: array().of(singlePuzzleSchema),
    coloringGrids: array().of(singleGridSchema),
    artist: updateSchema(artistId, artistData, {
      removeDescriptionLengthValidators: true,
      removeArtistDisplayNameLengthValidators: true,
    })
      .concat(
        object({
          country: reactSelectValidator(),
          [UpdateField.RejectMessage]: string().optional(),
          [UpdateField.RevenueShare]: number()
            .transform((v) => (isNaN(v) ? undefined : v))
            .min(0, 'Minimum revenue share is 0')
            .max(100, 'Maximum revenue share is 100')
            .when('status', {
              is: (option: Option) => option.value === Status.Approved,
              then: (schema) => schema.required('Must set revenue share when approving artist'),
            }),
        }),
      )
      .test('artist-status-validity-test', (value, context) => {
        // TODO This is preventing run, those fixes are not part of the ticket
        const artistStatus = (value as any)?.status?.value;
        const hasApprovedContent =
          context.from?.[1].value.puzzles?.some(
            (puzzle: PuzzleFormFields) => puzzle?.status?.value === 'approved',
          ) ||
          context.from?.[1].value.coloringGrids?.some(
            (coloringGrid: GridFormFields) => coloringGrid?.status?.value === 'approved',
          );
        const rejectMessage = value.rejectMessage;

        switch (artistStatus) {
          case Status.Approved:
            if (!hasApprovedContent) {
              throw new ValidationError(
                'Must approve at least 1 content to approve artist',
                artistStatus,
                `${context.path}.status`,
                'string',
              );
            }
            break;
          case Status.New:
          case Status.Pending:
            if (hasApprovedContent) {
              throw new ValidationError(
                'Artist has at least 1 approved content, proper status is approved',
                artistStatus,
                `${context.path}.status`,
                'string',
              );
            }
            break;
          case Status.Rejected:
            if (!rejectMessage) {
              throw new ValidationError(
                'Must provide reject reason, or change status',
                artistStatus,
                `${context.path}.status`,
                'string',
              );
            }
            break;
        }

        return true;
      }),
  });
};

export type FirstApprovalFormFields = InferType<ReturnType<typeof getFirstApprovalFormSchema>>;
